import { GridColumn } from '@progress/kendo-react-grid'
import classnames from 'classnames'
import { DashboardMenuOption } from 'components/dashboard-menu/constants'
import { FeatureUnavailableComponent } from 'components/feature-unavailable'
import { DateTimeCell } from 'components/gridcells/datetime'
import { HeaderComponent } from 'components/header/component'
import { UpgradeAnchorLink } from 'components/link'
import { RasaContext } from 'context'
import { BaseClientEntity } from 'generic/baseClientEntity'
import { Dataset } from 'generic/dataset'
import { EditableDropdownCell, EditCellProps } from 'generic/editCell'
import * as GenericRedux from 'generic/genericRedux'
import {RasaDataGrid} from 'generic/rasaDataGrid'
import * as GA from 'google-analytics'
import React, {Component} from 'react'
import { Button } from 'reactstrap'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { ModalSize } from 'shared/modals/constants'
import '../styles.css'
import { SourceSection } from '../types'
import { AddSourceSectionModal } from './modals'

type SourceSectionProps = GenericRedux.AllComponentPropsWithModal<SourceSection>

interface SourceSectionState {
  canAddSection: boolean,
  communityId: string,
  editId: string,
  editName: string,
  forceReload: boolean,
  hasFeatureAccess: boolean,
  maxSectionsCount: number,
  sections: any[],
}
class SourceSectionsComponent extends Component<SourceSectionProps, SourceSectionState> {
  public static contextType = RasaContext
  private entityName: string = 'section'
  private sharedStore: SharedStore
  constructor(props: SourceSectionProps) {
    super(props)
    this.state = {
      canAddSection: true,
      communityId: '',
      editId: null,
      editName: '',
      forceReload: false,
      hasFeatureAccess: false,
      maxSectionsCount: 0,
      sections: [],
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
    ]).then(([activeCommunity]) => {
      this.getSections(activeCommunity.communityId)
      .then((sections) => {
        this.setState({
          canAddSection: sections.length < activeCommunity.billingInfo.usageStats.currentPlanMaxSections,
          communityId: activeCommunity.communityId,
          hasFeatureAccess: activeCommunity.billingInfo.usageStats.currentPlanMaxSections > 0,
          sections,
          maxSectionsCount: activeCommunity.billingInfo.usageStats.currentPlanMaxSections,
        })
      })
    })
  }

  public render() {
    return (
      !this.state.hasFeatureAccess ?
        <FeatureUnavailableComponent source={GA.UpgradeSource.UseSection} /> :
      <div className="article-sections">
        <div className="header-container">
          <AddSourceSectionModal
            data={this.props.modals}
            closeModal={this.props.closeModal}
            createOrUpdate={this.createOrUpdate}
            saveModal={this.props.saveModal}
            updateModal={this.props.updateModal}
            name={this.state.editName}
            size={ModalSize.Small} />
          <HeaderComponent
            icon={'contentTool'}
            title={'SOURCES'}
            subTitle={'Sections'}
          />
          <div className={classnames('add-sections-button-text-section')} >
            <Button
              className={classnames('add-sections-button', this.state.canAddSection ? '' : 'disabled')}
              onClick={() =>
              this.props.openModal(AddSourceSectionModal.key, null)}>
              <span className="sections-button-text">
                Add New Section
              </span>
            </Button>
            {this.state.canAddSection ? null :
            <div>
              <UpgradeAnchorLink source={GA.UpgradeSource.AddSection}>
                Upgrade to add more sections
              </UpgradeAnchorLink>
            </div>}
          </div>
        </div>
        <div>
            <RasaDataGrid
                // Rasa Properties for entity, datasetName etc
                entityName="section"
                datasetName="sections"
                editDefaultState={true}
                forceReload={this.state.forceReload}
                // events
                sortable={false}
                pageable={false}
                data={[]}>
              <GridColumn title="Name"  field="name" width={350} />
              <GridColumn title="# Upcoming"  field="last_week_total" width={200} />
              <GridColumn field="created" title="Create Date" cell={DateTimeCell} width={350} />
              <GridColumn cell={this.SectionEditCell}/>
            </RasaDataGrid>
          </div>
      </div>
    )
  }
  protected SectionEditCell = (props: EditCellProps) => {
    return <EditableDropdownCell {...props} canEdit={true} onEdit={this.editClick}
            onArchive={this.onArchive} canArchive={true}/>
  }
  private editSectionUrl = (p: any) => `${DashboardMenuOption.content}/editsection?id=${p.id}`

  private editClick = (dataItem: any) => {
    this.props.push(this.editSectionUrl(dataItem))
  }
  private onArchive = (dataItem: any) => {
    this.context.entityMetadata.getEntityObject(this.entityName).then(
      (entityObject: BaseClientEntity) => {
        entityObject.setRecordId(this.state.communityId, dataItem.id)
        entityObject.archive()
        .then(() => {
          this.setState({
            forceReload: true,
          }, () => {
            this.setState({forceReload: false})
          })

        })
      })
  }
  private createOrUpdate = (payload: any) => {
    return this.context.entityMetadata
    .getEntityObject(this.entityName, this.state.communityId, null)
    .then((sourceSectionEntityObject: BaseClientEntity) => {
      sourceSectionEntityObject.setRecordId(this.state.communityId, this.state.editId ? this.state.editId : null)
      sourceSectionEntityObject.data.name = payload.name
      sourceSectionEntityObject.data.community_identifier = this.state.communityId
      return sourceSectionEntityObject.save()
        .then(() => {
          this.props.resetModal(AddSourceSectionModal.key)
          this.setState({
            forceReload: true,
            editId: null,
            editName: '',
          }, () => {
            this.setState({forceReload: false})
          })
          this.getSections(this.state.communityId)
          .then((sections) => {
            this.setState({
              canAddSection: sections.length < this.state.maxSectionsCount,
              sections,
            })
          })
        })
    })
  }
  private getSections = (community) => {
    return new Dataset()
    .loadCommunityDataset('sections', community)
    .then((response) => {
      return response[0] || []
    })
  }
}

// tslint:disable-next-line: variable-name
export const ContentPoolSourceSections = GenericRedux.registerNewComponentWithModals<SourceSection>(
  SourceSectionsComponent,
  'source_sections',
  [AddSourceSectionModal.key],
  )
